import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { PageLayout, PageSection } from "@/components/site/PageLayout";
import { Reveal } from "@/components/site/Reveal";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    ...pageHead({
      title: "Privacy Policy · SunSmart Engineering",
      description:
        "How SunSmart Engineering collects, uses and protects personal information, including data handling practices, security measures and your choices.",
      path: "/privacy-policy",
    }),
  }),
  component: PrivacyPolicyPage,
});

const SECTIONS = [
  { id: "who-we-are", label: "Who We Are" },
  { id: "comments", label: "Comments" },
  { id: "media", label: "Media" },
  { id: "cookies", label: "Cookies" },
  { id: "consent", label: "Consent" },
  { id: "information-we-collect", label: "Information We Collect" },
  { id: "how-we-use-your-information", label: "How We Use Your Information" },
  { id: "log-files", label: "Log Files" },
  { id: "cookies-and-web-beacons", label: "Cookies and Web Beacons" },
  { id: "advertising-partners-privacy-policies", label: "Advertising Partners Privacy Policies" },
  { id: "third-party-privacy-policies", label: "Third Party Privacy Policies" },
  { id: "ccpa-privacy-rights", label: "CCPA Privacy Rights" },
  { id: "gdpr-data-protection-rights", label: "GDPR Data Protection Rights" },
  { id: "childrens-information", label: "Children's Information" },
  { id: "changes-to-this-privacy-policy", label: "Changes to This Privacy Policy" },
  { id: "contact-us", label: "Contact Us" },
];

function PrivacyPolicyPage() {
  return (
    <PageLayout kicker="Legal" title="Privacy Policy">
      <PageSection>
        <div className="grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-3 lg:sticky lg:top-32 lg:self-start">
            <nav>
              <div className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-sun">On this page</div>
              <ul className="mt-4 space-y-2 border-l border-border pl-4">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="text-sm text-muted-foreground hover:text-sun transition-colors"
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </Reveal>

          <Reveal delay={80} className="lg:col-span-9">
            <div className="max-w-prose">
              <h2 id="who-we-are" className="font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Who We Are
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Our website address is: https://sunsmartengineering.com.
              </p>

              <h2 id="comments" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Comments
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                When visitors leave comments on the site we collect the data shown in the comments form, and
                also the visitor's IP address and browser user agent string to help spam detection.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                An anonymized string created from your email address (also called a hash) may be provided to
                the Gravatar service to see if you are using it. The Gravatar service privacy policy is
                available here: https://automattic.com/privacy/. After approval of your comment, your profile
                picture is visible to the public in the context of your comment.
              </p>

              <h2 id="media" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Media
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                If you upload images to the website, you should avoid uploading images with embedded location
                data (EXIF GPS) included. Visitors to the website can download and extract any location data
                from images on the website.
              </p>

              <h2 id="cookies" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Cookies
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                If you leave a comment on our site you may opt-in to saving your name, email address and
                website in cookies. These are for your convenience so that you do not have to fill in your
                details again when you leave another comment. These cookies will last for one year.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                If you visit our login page, we will set a temporary cookie to determine if your browser
                accepts cookies. This cookie contains no personal data and is discarded when you close your
                browser.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                When you log in, we will also set up several cookies to save your login information and your
                screen display choices. Login cookies last for two days, and screen options cookies last for a
                year. If you select "Remember Me", your login will persist for two weeks. If you log out of
                your account, the login cookies will be removed.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                If you edit or publish an article, an additional cookie will be saved in your browser. This
                cookie includes no personal data and simply indicates the post ID of the article you just
                edited. It expires after 1 day.
              </p>

              <h2 id="consent" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Consent
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                By using our website, you hereby consent to our Privacy Policy and agree to its terms.
              </p>

              <h2 id="information-we-collect" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Information We Collect
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                The personal information that you are asked to provide, and the reasons why you are asked to
                provide it, will be made clear to you at the point we ask you to provide your personal
                information. If you contact us directly, we may receive additional information about you such
                as your name, email address, phone number, the contents of the message and/or attachments you
                may send us, and any other information you may choose to provide.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                When you register for an Account, we may ask for your contact information, including items
                such as name, company name, address, email address, and telephone number.
              </p>

              <h2 id="how-we-use-your-information" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                How We Use Your Information
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                We use the information we collect in various ways, including to: Provide, operate, and
                maintain our website. Improve, personalize, and expand our website.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Understand and analyze how you use our website.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Develop new products, services, features, and functionality.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Communicate with you, either directly or through one of our partners, including for customer
                service, to provide you with updates and other information relating to the website, and for
                marketing and promotional purposes.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">Send you emails.</p>
              <p className="mt-4 leading-relaxed text-muted-foreground">Find and prevent fraud.</p>

              <h2 id="log-files" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Log Files
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                We follow a standard procedure of using log files. These files log visitors when they visit
                websites. All hosting companies do this and a part of hosting services' analytics. The
                information collected by log files include internet protocol (IP) addresses, browser type,
                Internet Service Provider (ISP), date and time stamp, referring/exit pages, and possibly the
                number of clicks. These are not linked to any information that is personally identifiable. The
                purpose of the information is for analyzing trends, administering the site, tracking users'
                movement on the website, and gathering demographic information.
              </p>

              <h2 id="cookies-and-web-beacons" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Cookies and Web Beacons
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Like any other website, we use "cookies". These cookies are used to store information
                including visitors' preferences, and the pages on the website that the visitor accessed or
                visited. The information is used to optimize the users' experience by customizing our web page
                content based on visitors' browser type and/or other information.
              </p>

              <h2 id="advertising-partners-privacy-policies" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Advertising Partners Privacy Policies
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                You may consult this list to find the Privacy Policy for each of the advertising partners.
                Third-party ad servers or ad networks uses technologies like cookies, JavaScript, or Web
                Beacons that are used in their respective advertisements and links that appear on our site,
                which are sent directly to users' browser. They automatically receive your IP address when
                this occurs. These technologies are used to measure the effectiveness of their advertising
                campaigns and/or to personalize the advertising content that you see on websites that you
                visit.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Note that our site has no access to or control over these cookies that are used by third-party
                advertisers.
              </p>

              <h2 id="third-party-privacy-policies" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Third Party Privacy Policies
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Our Privacy Policy does not apply to other advertisers or websites. Thus, we are advising you
                to consult the respective Privacy Policies of these third-party ad servers for more detailed
                information. It may include their practices and instructions about how to opt-out of certain
                options.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                You can choose to disable cookies through your individual browser options. To know more
                detailed information about cookie management with specific web browsers, it can be found at
                the browsers' respective websites.
              </p>

              <h2 id="ccpa-privacy-rights" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                CCPA Privacy Rights (Do Not Sell My Personal Information)
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Under the CCPA, among other rights, California consumers have the right to: Request that a
                business that collects a consumer's personal data disclose the categories and specific pieces
                of personal data that a business has collected about consumers.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Request that a business delete any personal data about the consumer that a business has
                collected.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Request that a business that sells a consumer's personal data, not sell the consumer's
                personal data. If you make a request, we have one month to respond to you. If you would like
                to exercise any of these rights, please contact us.
              </p>

              <h2 id="gdpr-data-protection-rights" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                GDPR Data Protection Rights
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                We would like to make sure you are fully aware of all of your data protection rights. Every
                user is entitled to the following:
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                The right to access. You have the right to request copies of your personal data. We may
                charge you a small fee for this service.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                The right to rectification. You have the right to request that we correct any information you
                believe is inaccurate. You also have the right to request that we complete information you
                believe is incomplete.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                The right to erasure. You have the right to request that we erase your personal data, under
                certain conditions.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                The right to restrict processing. You have the right to request that we restrict the
                processing of your personal data, under certain conditions.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                The right to object to processing. You have the right to object to our processing of your
                personal data, under certain conditions.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                The right to data portability. You have the right to request that we transfer the data that
                we have collected to another organization, or directly to you, under certain conditions.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                If you make a request, we have one month to respond to you. If you would like to exercise any
                of these rights, please contact us.
              </p>

              <h2 id="childrens-information" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Children's Information
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Another part of our priority is adding protection for children while using the internet. We
                encourage parents and guardians to observe, participate in, and/or monitor and guide their
                online activity. We do not knowingly collect any Personal Identifiable Information from
                children under the age of 18. If you think that your child provided this kind of information
                on our website, we strongly encourage you to contact us immediately and we will do our best
                efforts to promptly remove such information from our records.
              </p>

              <h2 id="changes-to-this-privacy-policy" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Changes to This Privacy Policy
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                We may update our Privacy Policy from time to time. Thus, we advise you to review this page
                periodically for any changes. We will notify you of any changes by posting the new Privacy
                Policy on this page. These changes are effective immediately, after they are posted on this
                page.
              </p>

              <h2 id="contact-us" className="mt-12 font-display text-2xl font-black uppercase leading-tight scroll-mt-32">
                Contact Us
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                If you have any questions or suggestions about our Privacy Policy, do not hesitate to{" "}
                <Link
                  to="/contact"
                  className="text-sun hover:text-foreground transition-colors underline underline-offset-2"
                >
                  contact us
                </Link>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </PageSection>
    </PageLayout>
  );
}
